import React, { useCallback, useState } from 'react';

import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useAppsFlyerIntegration from 'api/useAppsFlyerIntegration';
import { EToasterStatus, showToast } from 'utils/showToast';

import { Button } from 'components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle
} from 'components/ui/card';

import {
  appsFlyerS2SDocsUrl,
  toastMessages
} from '../../DeveloperSettings.constants';

import { AppsFlyerS2SDrawer } from './AppsFlyerS2SDrawer';
import { AppsFlyerS2SFormValues, AppsFlyerS2SSectionProps } from './types';

export const AppsFlyerS2SSection: React.FC<AppsFlyerS2SSectionProps> = ({
  canManage = true
}) => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { getIntegration, upsert, disable } = useAppsFlyerIntegration(
    publisherId,
    { enabled: canManage }
  );

  const handleOpenDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const handleCloseDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const handleOpenDocs = useCallback(() => {
    window.open(appsFlyerS2SDocsUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleSave = useCallback(
    async (values: AppsFlyerS2SFormValues) => {
      try {
        await upsert.mutateAsync(values);
        showToast({
          message: toastMessages.appsFlyerS2SSuccess,
          status: EToasterStatus.SUCCESS
        });
        getIntegration.refetch();
        setIsDrawerOpen(false);
      } catch (err: any) {
        const status = err?.response?.status;
        const serverErrors = err?.response?.data?.errors;
        if (status === 400 && Array.isArray(serverErrors)) {
          throw serverErrors;
        }
        showToast({
          message:
            status === 409
              ? toastMessages.appsFlyerS2SAppIdConflict
              : toastMessages.appsFlyerS2SError,
          status: EToasterStatus.ERROR
        });
      }
    },
    [upsert, getIntegration]
  );

  const handleDisable = useCallback(() => {
    disable.mutate(undefined, {
      onSuccess: () => {
        showToast({
          message: toastMessages.appsFlyerS2SDisabled,
          status: EToasterStatus.SUCCESS
        });
        getIntegration.refetch();
        setIsDrawerOpen(false);
      },
      onError: (err: any) => {
        if (err?.response?.status === 404) {
          setIsDrawerOpen(false);
          return;
        }
        showToast({
          message: toastMessages.appsFlyerS2SError,
          status: EToasterStatus.ERROR
        });
      }
    });
  }, [disable, getIntegration]);

  return (
    <>
      <Card className="w-full border border-base-muted-foreground shadow-none">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 py-5">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-base font-semibold text-gray-900">
              AppsFlyer S2S Integration
            </CardTitle>
            <CardDescription className="text-sm text-gray-500">
              Integrate web store purchases to mobile acquisition campaigns via
              AppsFlyer.
            </CardDescription>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleOpenDocs}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
              data-testid="apps-flyer-s2s-docs"
            >
              Docs
              <FontAwesomeIcon icon="fa-regular fa-file-lines" />
            </button>

            <Button
              variant="default"
              onClick={handleOpenDrawer}
              disabled={!canManage}
              className="h-9 rounded-lg bg-gray-900 px-4 font-medium text-white hover:bg-gray-800 disabled:bg-gray-400"
              data-testid="apps-flyer-s2s-open-settings"
            >
              Open Settings
            </Button>
          </div>
        </CardHeader>
      </Card>

      <AppsFlyerS2SDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        initialValues={getIntegration.data ?? undefined}
        onSave={handleSave}
        onDisable={handleDisable}
        isSaving={upsert.isLoading || disable.isLoading}
      />
    </>
  );
};
